// ═══════════════════════════════════════════════════════════════════════
// benefitsTab.jsx — Interactive CLM benefits checklist
// ═══════════════════════════════════════════════════════════════════════
// 76 benefits across 14 categories. Each benefit is checkable, tag-filterable
// (category, phase, audience, monitored), assignable to a stakeholder team,
// and supports per-row notes. State persists via the reducer.
//
// Reducer actions used:
//   - TOGGLE_BENEFIT       { id }
//   - SET_BENEFIT_OWNER    { id, owner }
//   - SET_BENEFIT_NOTE     { id, note }
//
// ═══════════════════════════════════════════════════════════════════════

import React, { useMemo, useState } from "react";
import {
  BENEFITS, BENEFIT_CATEGORIES, PHASES, AUDIENCES,
  topMonitored, topNonMonitored,
} from "./benefitsData";
import { T, FONTS } from "./theme";
import { getInterfacesForBenefit } from "./crossRefs";
import { EntityRefList } from "./navigation";

const STAKEHOLDER_TEAMS = [
  { id: "exec",    label: "Executive sponsor",   color: T.gold },
  { id: "eng",     label: "Engineering",         color: T.cyan },
  { id: "mfg",     label: "Manufacturing",       color: T.orange },
  { id: "qual",    label: "Quality",             color: T.pink },
  { id: "fin",     label: "Finance",             color: T.amber },
  { id: "it",      label: "IT / Integration",    color: T.blue },
  { id: "ops",     label: "Operations",          color: T.green },
  { id: "scm",     label: "Supply Chain",        color: T.purple },
  { id: "esg",     label: "Sustainability",      color: T.teal },
];

export default function BenefitsTab({ state, dispatch }) {
  const benefitsState = state.benefitsState || {};
  const benefitNotes  = state.benefitNotes  || {};
  const benefitOwners = state.benefitOwners || {};

  const [filterCat, setFilterCat]           = useState("ALL");
  const [filterPhase, setFilterPhase]       = useState("ALL");
  const [filterAudience, setFilterAudience] = useState("ALL");
  const [filterMon, setFilterMon]           = useState("ALL"); // ALL | YES | NO
  const [filterDone, setFilterDone]         = useState("ALL"); // ALL | DONE | OPEN
  const [search, setSearch]                 = useState("");
  const [expanded, setExpanded]             = useState(new Set());
  const [showTop, setShowTop]               = useState(false);

  const filtered = useMemo(() => {
    return BENEFITS.filter(b => {
      if (filterCat !== "ALL" && b.cat !== filterCat) return false;
      if (filterPhase !== "ALL" && b.phase !== filterPhase) return false;
      if (filterAudience !== "ALL" && b.audience !== filterAudience) return false;
      if (filterMon === "YES" && !b.monitored) return false;
      if (filterMon === "NO"  &&  b.monitored) return false;
      const done = !!benefitsState[b.id];
      if (filterDone === "DONE" && !done) return false;
      if (filterDone === "OPEN" &&  done) return false;
      if (search) {
        const hay = `${b.id} ${b.name} ${b.meaning} ${(b.keywords || []).join(" ")}`.toLowerCase();
        if (!hay.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [filterCat, filterPhase, filterAudience, filterMon, filterDone, search, benefitsState]);

  const checkedCount  = filtered.filter(b => benefitsState[b.id]).length;
  const totalChecked  = BENEFITS.filter(b => benefitsState[b.id]).length;
  const pctChecked    = Math.round((totalChecked / BENEFITS.length) * 100);

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const ns = new Set(prev);
      if (ns.has(id)) ns.delete(id); else ns.add(id);
      return ns;
    });
  };

  const onCheck = (id, e) => {
    e.stopPropagation();
    dispatch({ type: "TOGGLE_BENEFIT", id });
  };

  return (
    <div style={{ padding: "20px 24px", maxHeight: "calc(100vh - 108px)", overflowY: "auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 16, fontWeight: 700, color: T.cyan, marginBottom: 4 }}>
          🎯 CLM Benefits Checklist
        </div>
        <div style={{ fontSize: 11, color: T.text2 }}>
          {BENEFITS.length} benefits across {BENEFIT_CATEGORIES.length} categories ·
          {" "}monitored ✓ ({BENEFITS.filter(b => b.monitored).length}) and felt-but-unmeasured ✗ ({BENEFITS.filter(b => !b.monitored).length}) ·
          {" "}During build vs upon steady-state vs both ·
          {" "}Customer-sponsor lens vs adopter-operator lens
        </div>
      </div>

      {/* KPI strip */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        {[
          { l: "Total benefits", v: BENEFITS.length, c: T.cyan },
          { l: "Achieved",  v: totalChecked, c: T.green },
          { l: "Progress",  v: `${pctChecked}%`, c: pctChecked === 100 ? T.green : T.amber },
          { l: "Monitored", v: BENEFITS.filter(b => b.monitored).length, c: T.blue },
          { l: "Felt-only", v: BENEFITS.filter(b => !b.monitored).length, c: T.purple },
          { l: "Filtered",  v: filtered.length, c: T.gold },
        ].map(k => (
          <div key={k.l} style={{
            background: T.bg2, border: `1px solid ${k.c}30`, borderRadius: 8,
            padding: "10px 16px", minWidth: 80,
          }}>
            <div style={{ fontFamily: FONTS.header, fontSize: 18, fontWeight: 700, color: k.c }}>{k.v}</div>
            <div style={{ fontSize: 8, color: T.text2, letterSpacing: 1, textTransform: "uppercase" }}>{k.l}</div>
          </div>
        ))}
      </div>

      {/* Overall progress bar */}
      <div style={{
        background: T.bg2, border: `1px solid ${T.border}`,
        borderRadius: 8, padding: "14px 18px", marginBottom: 20,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
          <span style={{ fontWeight: 600, fontSize: 12 }}>Realization progress</span>
          <span style={{ fontFamily: FONTS.header, fontSize: 14, fontWeight: 700,
                         color: pctChecked === 100 ? T.green : T.cyan }}>
            {totalChecked} / {BENEFITS.length}  ·  {pctChecked}%
          </span>
        </div>
        <div style={{ height: 6, background: T.bg3, borderRadius: 3, overflow: "hidden" }}>
          <div style={{
            height: "100%", width: `${pctChecked}%`,
            background: `linear-gradient(90deg, ${T.cyan}, ${T.green})`, borderRadius: 3,
            transition: "width .3s ease",
          }} />
        </div>
      </div>

      {/* Filter bar */}
      <div style={{
        display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap",
        padding: "12px 14px", background: T.bg2, borderRadius: 8, border: `1px solid ${T.border}`,
      }}>
        <input type="text" placeholder="🔍 Search name, meaning, keyword..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{
            background: T.bg3, border: `1px solid ${T.border}`, color: T.text,
            padding: "5px 10px", borderRadius: 4, fontSize: 10, outline: "none", minWidth: 240,
          }} />

        <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={selectStyle}>
          <option value="ALL">All categories ({BENEFITS.length})</option>
          {BENEFIT_CATEGORIES.map(c => (
            <option key={c.id} value={c.id}>{c.icon} {c.label} ({BENEFITS.filter(b => b.cat === c.id).length})</option>
          ))}
        </select>

        <select value={filterPhase} onChange={e => setFilterPhase(e.target.value)} style={selectStyle}>
          <option value="ALL">All phases</option>
          {PHASES.map(p => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>

        <select value={filterAudience} onChange={e => setFilterAudience(e.target.value)} style={selectStyle}>
          <option value="ALL">All audiences</option>
          {AUDIENCES.map(a => (
            <option key={a.id} value={a.id}>{a.label}</option>
          ))}
        </select>

        <select value={filterMon} onChange={e => setFilterMon(e.target.value)} style={selectStyle}>
          <option value="ALL">Monitored & felt</option>
          <option value="YES">✓ Monitored only</option>
          <option value="NO">✗ Felt only</option>
        </select>

        <select value={filterDone} onChange={e => setFilterDone(e.target.value)} style={selectStyle}>
          <option value="ALL">All status</option>
          <option value="DONE">✓ Achieved</option>
          <option value="OPEN">○ Open</option>
        </select>

        <button onClick={() => setShowTop(s => !s)} style={{
          ...selectStyle,
          background: showTop ? `${T.gold}15` : T.bg3,
          color: showTop ? T.gold : T.text,
          border: `1px solid ${showTop ? T.gold : T.border}`,
        }}>
          🏆 Top 30 lens
        </button>

        <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
          <span style={{ fontSize: 10, color: T.text2 }}>
            {filtered.length}/{BENEFITS.length} shown · {checkedCount} checked
          </span>
        </div>
      </div>

      {/* Top 30 lens — two columns */}
      {showTop && (
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16,
        }}>
          <div style={{ background: T.bg2, border: `1px solid ${T.gold}30`, borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.gold, marginBottom: 8 }}>
              🏆 Top 15 monitored — the business-case spine
            </div>
            <table style={{ width: "100%", fontSize: 9 }}>
              <thead><tr style={{ borderBottom: `1px solid ${T.border}` }}>
                <th style={thS}>#</th><th style={thS}>ID</th><th style={thS}>Benefit</th><th style={thS}>Target</th><th style={thS}></th>
              </tr></thead>
              <tbody>
                {topMonitored().map(b => {
                  const cat = BENEFIT_CATEGORIES.find(c => c.id === b.cat);
                  return (
                    <tr key={b.id} style={{ borderBottom: `1px solid ${T.border2}` }}>
                      <td style={tdS}>{b.topMonitored}</td>
                      <td style={{ ...tdS, color: cat?.color, fontWeight: 600 }}>{b.id}</td>
                      <td style={tdS}>{b.name}</td>
                      <td style={{ ...tdS, color: T.green, fontSize: 8 }}>{b.target}</td>
                      <td style={tdS}>
                        <input type="checkbox" checked={!!benefitsState[b.id]}
                          onChange={e => onCheck(b.id, e)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{ background: T.bg2, border: `1px solid ${T.purple}30`, borderRadius: 8, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.purple, marginBottom: 8 }}>
              ✨ Top 15 felt — the trust-and-adoption spine
            </div>
            <table style={{ width: "100%", fontSize: 9 }}>
              <thead><tr style={{ borderBottom: `1px solid ${T.border}` }}>
                <th style={thS}>#</th><th style={thS}>ID</th><th style={thS}>Benefit</th><th style={thS}>Shows up in...</th><th style={thS}></th>
              </tr></thead>
              <tbody>
                {topNonMonitored().map(b => {
                  const cat = BENEFIT_CATEGORIES.find(c => c.id === b.cat);
                  return (
                    <tr key={b.id} style={{ borderBottom: `1px solid ${T.border2}` }}>
                      <td style={tdS}>{b.topNonMonitored}</td>
                      <td style={{ ...tdS, color: cat?.color, fontWeight: 600 }}>{b.id}</td>
                      <td style={tdS}>{b.name}</td>
                      <td style={{ ...tdS, color: T.text2, fontSize: 8, fontStyle: "italic" }}>{b.metric}</td>
                      <td style={tdS}>
                        <input type="checkbox" checked={!!benefitsState[b.id]}
                          onChange={e => onCheck(b.id, e)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Benefits grouped by category */}
      {BENEFIT_CATEGORIES.map(cat => {
        const inCat = filtered.filter(b => b.cat === cat.id);
        if (inCat.length === 0) return null;
        const doneInCat = inCat.filter(b => benefitsState[b.id]).length;
        const pctInCat  = inCat.length > 0 ? Math.round((doneInCat / inCat.length) * 100) : 0;
        return (
          <div key={cat.id} style={{ marginBottom: 20 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 12, marginBottom: 10,
              padding: "8px 14px", background: `${cat.color}10`,
              borderLeft: `4px solid ${cat.color}`, borderRadius: 4,
            }}>
              <span style={{ fontSize: 20 }}>{cat.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: FONTS.header, fontSize: 12, fontWeight: 700, color: cat.color }}>
                  {cat.label}
                </div>
                <div style={{ fontSize: 9, color: T.text2 }}>{cat.scope}</div>
              </div>
              <div style={{ fontSize: 10, color: cat.color, fontWeight: 600 }}>
                {doneInCat} / {inCat.length} · {pctInCat}%
              </div>
              <div style={{ width: 120, height: 4, background: T.bg3, borderRadius: 2 }}>
                <div style={{ height: "100%", width: `${pctInCat}%`, background: cat.color, borderRadius: 2 }}/>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", gap: 10 }}>
              {inCat.map(b => {
                const isOpen = expanded.has(b.id);
                const isDone = !!benefitsState[b.id];
                const phaseMeta = PHASES.find(p => p.id === b.phase);
                const audMeta   = AUDIENCES.find(a => a.id === b.audience);
                const owner     = benefitOwners[b.id];
                const note      = benefitNotes[b.id] || "";
                return (
                  <div key={b.id} style={{
                    background: T.bg2,
                    border: `1px solid ${isDone ? T.green + "60" : T.border}`,
                    borderLeft: `3px solid ${cat.color}`,
                    borderRadius: 6, padding: "10px 12px",
                    opacity: isDone ? 0.92 : 1,
                  }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <div onClick={e => onCheck(b.id, e)}
                        style={{
                          width: 18, height: 18, borderRadius: 4, marginTop: 2,
                          border: `2px solid ${isDone ? T.green : T.border}`,
                          background: isDone ? T.green : "transparent",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          cursor: "pointer", flexShrink: 0,
                        }}>
                        {isDone && <span style={{ color: "#000", fontSize: 12, fontWeight: 900 }}>✓</span>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }} onClick={() => toggleExpand(b.id)}>
                        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                          <span style={{ fontFamily: FONTS.header, fontSize: 9, color: cat.color, fontWeight: 700 }}>
                            {b.id}
                          </span>
                          <span style={{ fontSize: 11, fontWeight: 600, color: isDone ? T.text2 : T.text,
                                         textDecoration: isDone ? "line-through" : "none" }}>
                            {b.name}
                          </span>
                          {b.topMonitored && <span style={pillStyle(T.gold)}>🏆 #{b.topMonitored}</span>}
                          {b.topNonMonitored && <span style={pillStyle(T.purple)}>✨ #{b.topNonMonitored}</span>}
                        </div>
                        <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.45, marginBottom: 6 }}>
                          {b.meaning}
                        </div>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                          <span style={pillStyle(b.monitored ? T.green : T.purple)}>
                            {b.monitored ? "✓ Monitored" : "✗ Felt"}
                          </span>
                          {phaseMeta && <span style={pillStyle(phaseMeta.color)}>{phaseMeta.id}</span>}
                          {audMeta   && <span style={pillStyle(audMeta.color)}>{audMeta.id}</span>}
                          {owner && <span style={pillStyle(T.amber)}>👤 {owner}</span>}
                          {note && <span style={pillStyle(T.cyan)}>📝</span>}
                        </div>
                      </div>
                      <span onClick={() => toggleExpand(b.id)} style={{
                        color: cat.color, cursor: "pointer", fontSize: 14,
                        transform: isOpen ? "rotate(90deg)" : "none",
                        transition: "transform .15s",
                      }}>▸</span>
                    </div>

                    {isOpen && (
                      <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border2}`, fontSize: 9.5 }}>
                        {b.monitored && b.target && (
                          <div style={{ marginBottom: 6 }}>
                            <strong style={{ color: T.green }}>🎯 Target:</strong>{" "}
                            <span style={{ color: T.text }}>{b.target}</span>
                            <span style={{ color: T.text3, marginLeft: 6 }}>· metric: {b.metric}</span>
                          </div>
                        )}
                        {!b.monitored && b.metric && (
                          <div style={{ marginBottom: 6 }}>
                            <strong style={{ color: T.purple }}>✨ Felt as:</strong>{" "}
                            <span style={{ fontStyle: "italic", color: T.text2 }}>{b.metric}</span>
                          </div>
                        )}
                        <div style={{ marginBottom: 6 }}>
                          <strong style={{ color: T.cyan }}>🏷️ Keywords:</strong>{" "}
                          <span style={{ color: T.text2 }}>{b.keywords.join(" · ")}</span>
                        </div>
                        <div style={{ marginBottom: 6 }}>
                          <strong style={{ color: T.amber }}>💬 Verb/noun pairs:</strong>{" "}
                          <span style={{ color: T.text2, fontStyle: "italic" }}>{b.verbs.join(" · ")}</span>
                        </div>
                        {b.rationale && (
                          <div style={{ marginBottom: 8 }}>
                            <strong style={{ color: T.steel }}>🎬 Rationale:</strong>{" "}
                            <span style={{ color: T.text2 }}>{b.rationale}</span>
                          </div>
                        )}

                        {/* NEW: Cross-tab nav — interfaces that deliver this benefit */}
                        <div style={{ marginBottom: 10 }}>
                          <EntityRefList
                            kind="interface"
                            ids={getInterfacesForBenefit(b.id)}
                            label="🔗 Interfaces that deliver this"
                            emptyText="(felt-only benefit — no direct interface mapping)"
                          />
                        </div>

                        <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
                          <label style={{ fontSize: 9, color: T.text2, minWidth: 60 }}>👤 Owner:</label>
                          <select
                            value={owner || ""}
                            onChange={e => dispatch({ type: "SET_BENEFIT_OWNER", id: b.id, owner: e.target.value })}
                            style={selectStyle}>
                            <option value="">(not assigned)</option>
                            {STAKEHOLDER_TEAMS.map(t => (
                              <option key={t.id} value={t.label}>{t.label}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label style={{ fontSize: 9, color: T.text2, display: "block", marginBottom: 4 }}>📝 Notes / evidence:</label>
                          <textarea
                            value={note}
                            onChange={e => dispatch({ type: "SET_BENEFIT_NOTE", id: b.id, note: e.target.value })}
                            placeholder="Capture progress notes, evidence, blockers..."
                            style={{
                              width: "100%", minHeight: 40, padding: "6px 8px",
                              background: T.bg3, border: `1px solid ${T.border}`, color: T.text,
                              fontSize: 9, borderRadius: 4, outline: "none", resize: "vertical",
                            }} />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {filtered.length === 0 && (
        <div style={{ padding: 40, textAlign: "center", color: T.text3, fontStyle: "italic" }}>
          No benefits match the current filters. Try clearing some.
        </div>
      )}

      {/* Footer guidance */}
      <div style={{
        marginTop: 30, padding: "14px 18px",
        background: T.bg2, border: `1px solid ${T.cyan}30`, borderRadius: 8,
      }}>
        <div style={{ fontSize: 10, color: T.cyan, fontWeight: 700, marginBottom: 6 }}>
          💡 How to use this checklist
        </div>
        <div style={{ fontSize: 9.5, color: T.text2, lineHeight: 1.6 }}>
          <strong style={{ color: T.text }}>For sponsors (CIO, CFO, COO, Plant Director):</strong> commit to 6–8 from the Top 15 monitored as the headline business case.
          Pick 3–4 from the Top 15 felt as cultural goals. Don't try to commit to all of them.
          <br/><br/>
          <strong style={{ color: T.text }}>For adopters (engineering, MFG, quality, finance, IT):</strong> walk through the list with each team and tag which 5–7 they own.
          The verb/noun pairs become the language of training and change management.
          <br/><br/>
          <em>The single best signal CLM is working isn't on any dashboard.
          It's when meetings shift from "what is the number?" to "what should we do about the number?"</em>
        </div>
      </div>
    </div>
  );
}

const selectStyle = {
  background: "#081525",
  border: `1px solid ${T.border}`,
  color: T.text,
  padding: "5px 8px",
  borderRadius: 4,
  fontSize: 9.5,
  outline: "none",
  cursor: "pointer",
};
const pillStyle = (c) => ({
  display: "inline-block", padding: "1px 7px", borderRadius: 10,
  fontSize: 8, fontFamily: FONTS.mono,
  background: `${c}15`, color: c, border: `1px solid ${c}40`,
});
const thS = { textAlign: "left", padding: "4px 6px", fontSize: 8, color: T.text2, fontWeight: 600 };
const tdS = { padding: "4px 6px", fontSize: 9, color: T.text };
